import React, { useState, useCallback } from 'react';
import { api } from '@/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Progress } from '@/components/ui/progress';
import { Mail, Upload, FileText, AlertCircle, CheckCircle2, Loader2 } from 'lucide-react';

const EmailDropZone = ({ 
  projectId, 
  moduleType = null, 
  moduleEntityId = null,
  onImportComplete,
  className = ""
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0, status: '' });
  const [results, setResults] = useState({ success: [], errors: [] });

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const files = Array.from(e.dataTransfer.files);
    const emailFiles = files.filter(f => 
      f.name.toLowerCase().endsWith('.eml') || 
      f.name.toLowerCase().endsWith('.msg')
    );
    
    if (emailFiles.length === 0) {
      toast.error('Please drop .eml or .msg email files');
      return;
    }
    
    // Open modal for import progress
    setIsModalOpen(true);
    setImporting(true);
    setProgress({ current: 0, total: emailFiles.length, status: 'Starting import...' });
    setResults({ success: [], errors: [] });
    
    // Import files one by one
    for (let i = 0; i < emailFiles.length; i++) {
      const file = emailFiles[i];
      setProgress({
        current: i + 1,
        total: emailFiles.length,
        status: `Importing ${file.name}...`
      });
      
      try {
        const formData = new FormData();
        formData.append('file', file);
        
        let url = `/correspondence/import?project_id=${projectId}`;
        if (moduleType) url += `&module_type=${moduleType}`;
        if (moduleEntityId) url += `&module_entity_id=${moduleEntityId}`;
        
        const response = await api.post(url, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        setResults(prev => ({
          ...prev,
          success: [...prev.success, { name: file.name, id: response.data.id }]
        }));
      } catch (error) {
        setResults(prev => ({
          ...prev,
          errors: [...prev.errors, { 
            name: file.name, 
            error: error.response?.data?.detail || 'Import failed' 
          }]
        }));
      }
    }
    
    setImporting(false);
    setProgress(prev => ({ ...prev, status: 'Import complete!' }));
    
    // Callback to refresh data
    if (onImportComplete) {
      onImportComplete();
    }
  }, [projectId, moduleType, moduleEntityId, onImportComplete]);

  const closeModal = () => {
    setIsModalOpen(false);
    setResults({ success: [], errors: [] });
  };

  return (
    <>
      {/* Drop Zone */}
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`
          border-2 border-dashed rounded-lg p-6 text-center transition-all cursor-pointer
          ${isDragging 
            ? 'border-indigo-500 bg-indigo-50' 
            : 'border-slate-200 hover:border-slate-300 bg-slate-50'
          }
          ${className}
        `}
        data-testid="email-drop-zone"
      >
        <div className="flex flex-col items-center gap-3">
          <div className={`
            w-12 h-12 rounded-full flex items-center justify-center
            ${isDragging ? 'bg-indigo-100 text-indigo-600' : 'bg-slate-100 text-slate-400'}
          `}>
            {isDragging ? <Upload className="h-6 w-6" /> : <Mail className="h-6 w-6" />}
          </div>
          <div>
            <p className={`font-medium ${isDragging ? 'text-indigo-600' : 'text-slate-700'}`}>
              {isDragging ? 'Drop emails here' : 'Add Email Correspondence'}
            </p>
            <p className="text-sm text-slate-500 mt-1">
              Drag & drop .eml or .msg files to import
            </p>
          </div>
        </div>
      </div>

      {/* Import Progress Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5 text-indigo-600" />
              Email Import
            </DialogTitle>
            <DialogDescription>
              {importing ? 'Importing and analyzing emails...' : 'Import complete'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Progress bar */}
            {progress.total > 0 && (
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-600">{progress.status}</span>
                  <span className="text-slate-500">{progress.current} / {progress.total}</span>
                </div>
                <Progress value={(progress.current / progress.total) * 100} />
              </div>
            )}

            {/* Results */}
            {!importing && (results.success.length > 0 || results.errors.length > 0) && (
              <div className="space-y-3">
                {/* Success */}
                {results.success.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-green-600 flex items-center gap-1">
                      <CheckCircle2 className="h-4 w-4" />
                      {results.success.length} imported successfully
                    </p>
                    <div className="max-h-32 overflow-y-auto space-y-1">
                      {results.success.map((item, idx) => (
                        <div key={idx} className="flex items-center gap-2 text-sm text-slate-600 bg-green-50 p-2 rounded overflow-hidden">
                          <FileText className="h-4 w-4 text-green-500 flex-shrink-0" />
                          <span className="truncate min-w-0" title={item.name}>{item.name}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Errors */}
                {results.errors.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-red-600 flex items-center gap-1">
                      <AlertCircle className="h-4 w-4" />
                      {results.errors.length} failed
                    </p>
                    <div className="max-h-32 overflow-y-auto space-y-1">
                      {results.errors.map((item, idx) => (
                        <div key={idx} className="text-sm bg-red-50 p-2 rounded overflow-hidden">
                          <p className="font-medium text-red-700 truncate" title={item.name}>{item.name}</p>
                          <p className="text-red-600 text-xs truncate" title={item.error}>{item.error}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Loading spinner */}
            {importing && (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
              </div>
            )}
          </div>

          {!importing && (
            <div className="flex justify-end">
              <Button onClick={closeModal}>Close</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default EmailDropZone;
